/* eslint-disable camelcase */
import posthog from 'posthog-js';
import { getHostProperty } from '../hooks/host';

export interface TelemetryUserData {
    id: string;
    name: string;
    email: string;
    createdAt: Date;
}

const state = { enabled: false };

const init = () => {
    const siteId = getHostProperty<string>('siteId');
    state.enabled = !!siteId;
    if (!state.enabled) return;

    posthog.init(siteId!, {
        api_host: 'https://eu.posthog.com', // Must match web-app exactly so the anon distinct_id is shared
        autocapture: false,
        capture_pageview: false, // We emit pageviews manually (see PageviewTracker in App.tsx)
        disable_session_recording: true,
        loaded: ph => ph.register({ platform: 'onboarding' }),
    });
};

const pageview = (props?: Record<string, unknown>) => {
    if (!state.enabled) return;
    posthog.capture('$pageview', props ?? {});
};

const identify = (user: TelemetryUserData) => {
    if (!state.enabled) return;

    // Anonymous sessions get a posthog-generated distinct_id. Alias it to the real
    // user id so the pre-auth onboarding funnel links to the identified user.
    const distinctId = posthog.get_distinct_id();
    const shouldAlias = !!distinctId && distinctId !== user.id;

    posthog.identify(user.id, {
        name: user.name,
        email: user.email,
        created_at: user.createdAt.toISOString(),
        last_login: new Date().toISOString(),
    });

    if (shouldAlias) {
        posthog.alias(user.id, distinctId);
    }
};

const emit = (event: string, data?: Record<string, unknown>) => {
    if (!state.enabled) return;
    try {
        posthog.capture(event, data ?? {});
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[telemetry] emit ${event} failed`, e);
    }
};

export const telemetry = Object.freeze({ init, pageview, identify, emit });
